import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface LearningCardProps {
  topicKey: string;
  compact?: boolean;
}

/**
 * LearningCard — renders educational content from the Learning Layer.
 * Compact mode: shows summary + link to full.
 * Full mode: shows complete structured content.
 */
export function LearningCard({ topicKey, compact = false }: LearningCardProps) {
  const [topic, setTopic] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.learning
      .get(topicKey)
      .then((data) => setTopic(data.topic))
      .catch(() => setTopic(null))
      .finally(() => setLoading(false));
  }, [topicKey]);

  if (loading) return null;
  if (!topic) return null;

  if (compact) {
    return (
      <div
        className="card-raised"
        style={{
          borderLeft: '3px solid var(--accent)',
          paddingLeft: '1.25rem',
        }}
      >
        <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>
          Aprende · {topic.category}
        </div>
        <h4 style={{ marginBottom: '0.5rem' }}>{topic.title}</h4>
        <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', lineHeight: 1.6 }}>
          {topic.summary}
        </p>
      </div>
    );
  }

  const sources = Array.isArray(topic.official_sources) ? topic.official_sources : [];
  const relatedTopics = Array.isArray(topic.related_topics) ? topic.related_topics : [];
  const requiredDocs = Array.isArray(topic.required_docs) ? topic.required_docs : [];

  return (
    <article className="card" style={{ padding: '2.5rem' }}>
      <div className="eyebrow">{topic.category}</div>
      <h2 style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>{topic.title}</h2>
      <div className="rule-gold" />
      {topic.summary && (
        <p style={{ fontSize: '1.05rem', color: 'var(--text-dim)', marginBottom: '2rem', lineHeight: 1.7 }}>
          {topic.summary}
        </p>
      )}

      <div className="stack-md">
        <InfoBlock label="¿Qué es?" content={topic.what_is} />
        <InfoBlock label="¿Por qué aplica?" content={topic.why_applies} />
        <InfoBlock label="Vencimiento" content={topic.deadline_info} mono />
        <InfoBlock label="Costo de cumplir" content={topic.cost_info} mono />
        <InfoBlock label="Multa si no se cumple" content={topic.penalty_info} danger />
        <InfoBlock label="Cómo cumplir" content={topic.how_to_comply} />

        {requiredDocs.length > 0 && (
          <div>
            <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Documentos requeridos</div>
            <ul style={{ listStyle: 'none', padding: 0, color: 'var(--text-dim)' }}>
              {requiredDocs.map((d: string, i: number) => (
                <li
                  key={i}
                  style={{
                    padding: '0.5rem 0',
                    borderBottom: i < requiredDocs.length - 1 ? '1px solid var(--border)' : 'none',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.85rem',
                  }}
                >
                  — {d}
                </li>
              ))}
            </ul>
          </div>
        )}

        {topic.red_flags && <InfoBlock label="Banderas rojas" content={topic.red_flags} danger />}

        {sources.length > 0 && (
          <div>
            <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Fuentes oficiales</div>
            <div className="stack-sm">
              {sources.map((s: any, i: number) => (
                <div key={i}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>
                    {s.label} →
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {relatedTopics.length > 0 && (
          <div>
            <div className="eyebrow" style={{ marginBottom: '0.75rem' }}>Temas relacionados</div>
            <div className="flex" style={{ flexWrap: 'wrap' }}>
              {relatedTopics.map((t: string) => (
                <a key={t} href={`/learn/${t}`} style={{ fontSize: '0.85rem' }}>
                  {t}
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={{ paddingTop: '1.5rem', borderTop: '1px solid var(--border)', color: 'var(--text-subtle)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>
          v{topic.version} · revisado {topic.last_reviewed} · regulaciones al {topic.regulations_as_of}
        </div>
      </div>
    </article>
  );
}

function InfoBlock({
  label,
  content,
  mono = false,
  danger = false,
}: {
  label: string;
  content: string | null;
  mono?: boolean;
  danger?: boolean;
}) {
  if (!content) return null;
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: '0.5rem' }}>{label}</div>
      <p
        style={{
          color: danger ? 'var(--danger-bright)' : 'var(--text-dim)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
          fontSize: mono ? '0.9rem' : '0.95rem',
          lineHeight: 1.7,
          whiteSpace: 'pre-wrap',
        }}
      >
        {content}
      </p>
    </div>
  );
}

// ================================================================
// ContextualTip — small inline tooltip that links to LearningCard
// ================================================================

export function ContextualTip({
  topicKey,
  children,
}: {
  topicKey: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={`/learn/${topicKey}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        fontSize: '0.8rem',
        color: 'var(--accent)',
        fontFamily: 'var(--font-mono)',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      }}
    >
      <span style={{ fontSize: '0.65rem' }}>i</span>
      {children}
    </a>
  );
}
